import { useMemo, useRef, useState } from 'react';
import { mergeProps, useButton, useFocusRing } from 'react-aria';

import {
  getDeviceLightCapabilityIdList,
  getDeviceLightCapabilityIds,
  getDeviceLightCapabilityValues,
} from '../../lib/capabilities/deviceLightCapabilities';
import { getButtonImage } from '../controls/button/getButtonImage';

import { useHover } from '../../hooks/useHover.js';
import { useCapabilityValues } from '../../store/devices/useCapabilityValues';
import { useWebAppSettings } from '../../store/web-app-settings/useWebAppSettings';

import styles from './DeviceQuickAction.module.css';

import type { DeviceQuickActionProps } from './DeviceQuickAction.types';
import { getLightDeviceQuickActionColors } from './DeviceQuickActionColors';
import type { DeviceQuickActionStyle } from './DeviceQuickActionColors';
import { DeviceQuickActionIcon } from './DeviceQuickActionIcon';

export function DeviceQuickActionLightButton(props: DeviceQuickActionProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const [pulseVersion, setPulseVersion] = useState(0);
  const { settings } = useWebAppSettings();
  const focusRing = useFocusRing({});
  const hover = useHover({});
  const lightCapabilityIds = useMemo(() => {
    return getDeviceLightCapabilityIds({
      device: props.device,
      preferredCapabilityId: props.capabilityObj?.id,
    });
  }, [props.capabilityObj?.id, props.device]);
  const capabilityIds = useMemo(() => {
    return getDeviceLightCapabilityIdList(lightCapabilityIds);
  }, [lightCapabilityIds]);
  const [capabilityValues] = useCapabilityValues({
    deviceId: props.device?.id ?? '',
    capabilityIds,
  });
  const button = useButton(
    {
      'aria-label': props.label,
      isDisabled: props.isDisabled === true,
      onPress() {
        if (props.isDisabled === true) {
          return;
        }

        setPulseVersion((currentPulseVersion) => {
          return currentPulseVersion + 1;
        });
        props.onPress();
      },
    },
    ref,
  );

  const buttonProps =
    props.isDisabled === true
      ? mergeProps(button.buttonProps, focusRing.focusProps)
      : mergeProps(button.buttonProps, focusRing.focusProps, hover.hoverProps);
  const buttonImage =
    props.capabilityObj != null
      ? getButtonImage(props.capabilityObj.id, props.capabilityObj)
      : null;
  const style = useMemo(() => {
    const lightValues = getDeviceLightCapabilityValues({
      capabilityIds: lightCapabilityIds,
      values: capabilityValues,
    });
    const colors = getLightDeviceQuickActionColors({
      isActive: button.isPressed,
      isHovered: false,
      isLightTheme: settings.theme.darkMode !== true,
      lightValues,
    });
    const hoverColors = getLightDeviceQuickActionColors({
      isActive: button.isPressed,
      isHovered: true,
      isLightTheme: settings.theme.darkMode !== true,
      lightValues,
    });
    const pulseColors = getLightDeviceQuickActionColors({
      isActive: true,
      isHovered: false,
      isLightTheme: settings.theme.darkMode !== true,
      lightValues,
    });

    return {
      '--prop-background-color': colors.backgroundColor,
      '--prop-border-color': colors.borderColor,
      '--prop-hover-background-color': hoverColors.backgroundColor,
      '--prop-hover-border-color': hoverColors.borderColor,
      '--prop-icon-color': colors.iconColor,
      '--prop-icon-size': colors.iconSize,
      '--prop-hover-icon-color': hoverColors.iconColor,
      '--prop-outline-color': colors.outlineColor,
      '--prop-overlay-color': colors.overlayColor,
      '--prop-pulse-color': pulseColors.iconColor,
      '--prop-hover-overlay-color': hoverColors.overlayColor,
    } as DeviceQuickActionStyle;
  }, [button.isPressed, capabilityValues, lightCapabilityIds, settings.theme.darkMode]);

  return (
    <button
      {...buttonProps}
      ref={ref}
      className={styles.root}
      style={style}
      data-is-focus-visible={focusRing.isFocusVisible}
      data-is-hovered={props.isDisabled === true ? false : hover.isHovered}
      data-is-pressed={props.isDisabled !== true && button.isPressed}
      data-variant={props.variant}
      title={props.label}
      type="button"
    >
      {pulseVersion > 0 ? (
        <span key={pulseVersion} aria-hidden={true} className={styles.pulse} />
      ) : null}

      <DeviceQuickActionIcon
        iconObj={buttonImage?.iconObj}
        source={buttonImage?.url}
        useIconObj={buttonImage?.useIconObj === true}
      />
    </button>
  );
}
